import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Modal, ProgressBar, Row, Spinner, Table } from 'react-bootstrap';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import api from '../services/api';
import { useArea } from '../context/AreaContext';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import ProjectCostCollaboratorImportModal from '../components/ProjectCostCollaboratorImportModal';
import ProjectCostCollaboratorImportJiraUsersModal from '../components/ProjectCostCollaboratorImportJiraUsersModal';
import CompanyCollaboratorsPage from './projectCosts/company/CompanyCollaboratorsPage';
import CompanyRolesUsedPage from './projectCosts/company/CompanyRolesUsedPage';
import CompanyRulesPage from './projectCosts/company/CompanyRulesPage';
import CompanySummaryPage from './projectCosts/company/CompanySummaryPage';
import CompanyTemporalRatesPage from './projectCosts/company/CompanyTemporalRatesPage';
import ProjectsCostAnalysisPage from './projectCosts/ProjectsCostAnalysisPage';
import ProjectsCostCompanyLayout from './projectCosts/ProjectsCostCompanyLayout';
import ProjectsCostHistoryPage from './projectCosts/ProjectsCostHistoryPage';
import ProjectsCostHubPage from './projectCosts/ProjectsCostHubPage';
import ProjectsCostSettingsLayout from './projectCosts/ProjectsCostSettingsLayout';
import ProjectsCostShell from './projectCosts/ProjectsCostShell';
import SettingsCompaniesPage from './projectCosts/settings/SettingsCompaniesPage';
import SettingsDemandRatesPage from './projectCosts/settings/SettingsDemandRatesPage';
import SettingsRolesPage from './projectCosts/settings/SettingsRolesPage';
import {
  ProjectCostCalculationResponse,
  ProjectCostConfigExportPayload,
  ProjectCostConfigImportPayload,
  ProjectCostConfigImportResponse,
  ProjectCostJiraUserSearchItem,
  ProjectCostJiraUserSearchResponse,
  ProjectCostJiraWorklogAuthorItem,
  ProjectCostJiraWorklogAuthorsResponse,
  ProjectCostCollaboratorResolveJiraIdsResponse,
  ProjectCostManagementSnapshot,
  ProjectsNlQueryResponse,
} from '../types';
import './ProjectsCostManagement.css';
import {
  formatCurrencyFromCents,
  formatHours,
  formatPercent,
  getCompanyMappingPercent,
  matchesSearchTokens,
  paginateItems,
} from './projectCosts/utils';
import type { CompanyStats, CostModalType, CostTableKey, CostTableState, SavedCostAnalysis } from './projectCosts/types';
import { EXECUTIVE_MAPPING_MIN_COVERAGE_PCT, centsToReaisInput, parseReaisToCents } from './projectCosts/utils';

export type ProjectCostContextValue = Record<string, unknown>;

const ProjectCostContext = createContext<ProjectCostContextValue | null>(null);

const inferIsThirdPartyFromCompanyName = (companyName?: string | null): boolean => (
  !String(companyName || '').toLowerCase().includes('petronect')
);

export const useProjectCostContext = (): ProjectCostContextValue => {
  const ctx = useContext(ProjectCostContext);
  if (!ctx) {
    throw new Error('useProjectCostContext deve ser usado dentro de ProjectsCostManagement');
  }
  return ctx;
};

const ProjectsCostManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setArea } = useArea();
  const { showAlert, showConfirm } = useModal();
  const canAccessProjectsArea = user?.role === 'admin' || Boolean(user?.can_access_projects);
  const canAccessCosts = user?.role === 'admin' || (user?.role === 'analista' && canAccessProjectsArea);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configSyncing, setConfigSyncing] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [activeModal, setActiveModal] = useState<CostModalType>(null);
  const [snapshot, setSnapshot] = useState<ProjectCostManagementSnapshot | null>(null);
  const [calcResult, setCalcResult] = useState<ProjectCostCalculationResponse | null>(null);
  const [jqlInput, setJqlInput] = useState('resolution is empty and Project = "Portal" and issuetype = "Historia" ORDER BY created DESC, updated DESC');
  const [calcMaxResultsInput, setCalcMaxResultsInput] = useState('200');
  const [unlimitedIssues, setUnlimitedIssues] = useState(false);
  const [worklogStartDateInput, setWorklogStartDateInput] = useState('');
  const [worklogEndDateInput, setWorklogEndDateInput] = useState('');
  const [includeSubtaskWorklogs, setIncludeSubtaskWorklogs] = useState(true);
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);
  const [analysisCollaboratorsPage, setAnalysisCollaboratorsPage] = useState(1);
  const [analysisCollaboratorsPageSize, setAnalysisCollaboratorsPageSize] = useState(20);
  const [analysisIssuesPage, setAnalysisIssuesPage] = useState(1);
  const [analysisIssuesPageSize, setAnalysisIssuesPageSize] = useState(30);
  const [analysisHistory, setAnalysisHistory] = useState<SavedCostAnalysis[]>([]);
  const [criticalMarginPctThreshold, setCriticalMarginPctThreshold] = useState(30);
  const [executiveCoverageThresholdPct, setExecutiveCoverageThresholdPct] = useState(EXECUTIVE_MAPPING_MIN_COVERAGE_PCT);
  const [editingIssueKey, setEditingIssueKey] = useState('');
  const [issueObservationInput, setIssueObservationInput] = useState('');
  const [savingIssueObservation, setSavingIssueObservation] = useState(false);
  const [showObservationColumn, setShowObservationColumn] = useState(true);
  const [showCollaboratorImportModal, setShowCollaboratorImportModal] = useState(false);
  const [showCollaboratorJiraImportModal, setShowCollaboratorJiraImportModal] = useState(false);
  const [nlQuestion, setNlQuestion] = useState('');
  const [nlLoading, setNlLoading] = useState(false);
  const [nlError, setNlError] = useState('');
  const [nlResult, setNlResult] = useState<ProjectsNlQueryResponse | null>(null);
  const [analysisControlTab, setAnalysisControlTab] = useState<'jql' | 'ai'>('jql');
  const [resolveJiraStatus, setResolveJiraStatus] = useState({
    running: false,
    stage: 'idle' as 'idle' | 'preview' | 'confirm' | 'apply' | 'refresh' | 'done' | 'error',
    progressPct: 0,
    message: '',
  });
  const [showResolveJiraPreviewModal, setShowResolveJiraPreviewModal] = useState(false);
  const [resolveJiraPreview, setResolveJiraPreview] = useState<ProjectCostCollaboratorResolveJiraIdsResponse | null>(null);
  const [jiraWorklogAuthors, setJiraWorklogAuthors] = useState<ProjectCostJiraWorklogAuthorItem[]>([]);
  const [loadingJiraWorklogAuthors, setLoadingJiraWorklogAuthors] = useState(false);
  const [manualMappingSelection, setManualMappingSelection] = useState<Record<number, string>>({});
  const [manualMappingSearch, setManualMappingSearch] = useState<Record<number, string>>({});
  const [manualMappingRemoteOptions, setManualMappingRemoteOptions] = useState<Record<number, ProjectCostJiraUserSearchItem[]>>({});
  const [manualMappingRemoteLoading, setManualMappingRemoteLoading] = useState<Record<number, boolean>>({});
  const [mappingCollaboratorIdInProgress, setMappingCollaboratorIdInProgress] = useState<number | null>(null);
  const jiraWorklogAuthorYear = 2026;

  const [companyForm, setCompanyForm] = useState({
    name: '',
    description: '',
    default_hourly_rate: '',
  });
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    default_hourly_rate: '',
  });
  const [collaboratorForm, setCollaboratorForm] = useState({
    name: '',
    email: '',
    jira_account_id: '',
    jira_display_name: '',
    user_id: '',
    company_id: '',
    role_id: '',
    hourly_rate_override: '',
    is_third_party: false,
  });
  const [demandRateForm, setDemandRateForm] = useState({
    demand_type: 'OS',
    label: '',
    hourly_rate: '',
  });
  const [companyRoleRateForm, setCompanyRoleRateForm] = useState({
    company_id: '',
    role_id: '',
    hourly_rate: '',
    effective_start: '',
    effective_end: '',
    notes: '',
  });
  const [collaboratorRateForm, setCollaboratorRateForm] = useState({
    collaborator_id: '',
    hourly_rate: '',
    effective_start: '',
    effective_end: '',
    notes: '',
  });
  const [editingCompanyId, setEditingCompanyId] = useState<number | null>(null);
  const [editingRoleId, setEditingRoleId] = useState<number | null>(null);
  const [editingCollaboratorId, setEditingCollaboratorId] = useState<number | null>(null);
  const [editingDemandRateId, setEditingDemandRateId] = useState<number | null>(null);
  const [editingCompanyRoleRateId, setEditingCompanyRoleRateId] = useState<number | null>(null);
  const [editingCollaboratorRateId, setEditingCollaboratorRateId] = useState<number | null>(null);
  const [tableStates, setTableStates] = useState<Record<CostTableKey, CostTableState>>({
    companies: { query: '', page: 1, pageSize: 10 },
    roles: { query: '', page: 1, pageSize: 10 },
    collaborators: { query: '', page: 1, pageSize: 10 },
    demandRates: { query: '', page: 1, pageSize: 10 },
    companyRoleRates: { query: '', page: 1, pageSize: 10 },
    collaboratorRates: { query: '', page: 1, pageSize: 10 },
  });

  const companies = useMemo(() => snapshot?.companies ?? [], [snapshot]);
  const roles = useMemo(() => snapshot?.roles ?? [], [snapshot]);
  const collaborators = useMemo(() => snapshot?.collaborators ?? [], [snapshot]);
  const demandRates = useMemo(() => snapshot?.demand_rates ?? [], [snapshot]);
  const companyRoleRates = useMemo(() => snapshot?.company_role_rates ?? [], [snapshot]);
  const collaboratorRates = useMemo(() => snapshot?.collaborator_rates ?? [], [snapshot]);
  const observationColumnStorageKey = useMemo(() => `projects_cost_show_observation_column:${user?.email || 'anonymous'}`, [user?.email]);
  const analysisHistoryStorageKey = useMemo(() => `projects_cost_analysis_history:${user?.email || 'anonymous'}`, [user?.email]);

  const handleApiError = useCallback(
    (err: unknown, fallbackMessage: string) => {
      const error = err as { response?: { data?: { detail?: string } } };
      showAlert({
        title: 'Erro',
        message: error?.response?.data?.detail || fallbackMessage,
        variant: 'danger',
      });
    },
    [showAlert]
  );

  const loadSnapshot = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<ProjectCostManagementSnapshot>('/api/projects/costs/snapshot');
      setSnapshot(data);
    } catch (err: unknown) {
      handleApiError(err, 'Não foi possível carregar dados de gerenciamento de custo.');
    } finally {
      setLoading(false);
    }
  }, [handleApiError]);

  const handleExportCostConfig = useCallback(async () => {
    setConfigSyncing(true);
    try {
      const { data } = await api.get<ProjectCostConfigExportPayload>('/api/projects/costs/export');
      const fileName = `projects-costs-export-${new Date().toISOString().slice(0, 10)}.json`;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showAlert({
        title: 'Exportação concluída',
        message: 'Arquivo de custos exportado com sucesso.',
        variant: 'success',
      });
    } catch (err: unknown) {
      handleApiError(err, 'Não foi possível exportar as configurações de custos.');
    } finally {
      setConfigSyncing(false);
    }
  }, [handleApiError, showAlert]);

  const handleImportCostConfig = useCallback(async (file: File) => {
    setConfigSyncing(true);
    try {
      const raw = await file.text();
      const parsed = JSON.parse(raw) as Partial<ProjectCostConfigImportPayload>;
      const payload: ProjectCostConfigImportPayload = {
        schema_version: Number(parsed.schema_version || 1),
        dry_run: false,
        companies: Array.isArray(parsed.companies) ? parsed.companies : [],
        roles: Array.isArray(parsed.roles) ? parsed.roles : [],
        demand_rates: Array.isArray(parsed.demand_rates) ? parsed.demand_rates : [],
        company_role_rates: Array.isArray(parsed.company_role_rates) ? parsed.company_role_rates : [],
        collaborators: Array.isArray(parsed.collaborators) ? parsed.collaborators : [],
        collaborator_rates: Array.isArray(parsed.collaborator_rates) ? parsed.collaborator_rates : [],
      };
      const { data } = await api.post<ProjectCostConfigImportResponse>('/api/projects/costs/import', payload);
      await loadSnapshot();
      showAlert({
        title: 'Importação concluída',
        message: `Empresas: +${data.companies.created}/${data.companies.updated} upd | Colaboradores: +${data.collaborators.created}/${data.collaborators.updated} upd | Erros: ${data.errors.length}.`,
        variant: data.errors.length ? 'warning' : 'success',
      });
    } catch (err: unknown) {
      handleApiError(err, 'Não foi possível importar as configurações de custos.');
    } finally {
      setConfigSyncing(false);
    }
  }, [handleApiError, loadSnapshot, showAlert]);

  useEffect(() => {
    if (canAccessCosts) {
      setArea('projects');
      void loadSnapshot();
    }
  }, [canAccessCosts, loadSnapshot, setArea]);

  useEffect(() => {
    if (!canAccessCosts) return;
    try {
      const raw = localStorage.getItem(observationColumnStorageKey);
      if (raw == null) {
        setShowObservationColumn(true);
        return;
      }
      setShowObservationColumn(raw === '1');
    } catch {
      setShowObservationColumn(true);
    }
  }, [canAccessCosts, observationColumnStorageKey]);

  useEffect(() => {
    if (!canAccessCosts) return;
    localStorage.setItem(observationColumnStorageKey, showObservationColumn ? '1' : '0');
  }, [canAccessCosts, observationColumnStorageKey, showObservationColumn]);

  useEffect(() => {
    if (!canAccessCosts) return;
    try {
      const raw = localStorage.getItem(analysisHistoryStorageKey);
      if (!raw) {
        setAnalysisHistory([]);
        return;
      }
      const parsed = JSON.parse(raw) as SavedCostAnalysis[];
      if (!Array.isArray(parsed)) {
        setAnalysisHistory([]);
        return;
      }
      const sanitized = parsed
        .filter((item) => item && typeof item === 'object')
        .map((item) => ({
          id: String(item.id || ''),
          createdAt: String(item.createdAt || ''),
          jql: String(item.jql || ''),
          maxResults: Number(item.maxResults || 0),
          worklogStartDate: String(item.worklogStartDate || ''),
          worklogEndDate: String(item.worklogEndDate || ''),
          totalHours: Number(item.totalHours || 0),
          totalCostCents: Number(item.totalCostCents || 0),
          totalRevenueCents: Number(item.totalRevenueCents || 0),
          totalMarginCents: Number(item.totalMarginCents || 0),
          issuesCount: Number(item.issuesCount || 0),
          unmatchedHours: Number(item.unmatchedHours || 0),
        }))
        .filter((item) => item.id && item.createdAt && item.jql);
      setAnalysisHistory(sanitized.slice(0, 20));
    } catch {
      setAnalysisHistory([]);
    }
  }, [analysisHistoryStorageKey, canAccessCosts]);

  useEffect(() => {
    if (!canAccessCosts) return;
    localStorage.setItem(analysisHistoryStorageKey, JSON.stringify(analysisHistory.slice(0, 20)));
  }, [analysisHistory, analysisHistoryStorageKey, canAccessCosts]);

  const closeModal = useCallback(() => {
    setActiveModal(null);
    setEditingCompanyId(null);
    setEditingRoleId(null);
    setEditingCollaboratorId(null);
    setEditingDemandRateId(null);
    setEditingCompanyRoleRateId(null);
    setEditingCollaboratorRateId(null);
  }, []);

  const handleCreateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const defaultRate = parseReaisToCents(companyForm.default_hourly_rate);
      if (companyForm.default_hourly_rate && defaultRate == null) {
        showAlert({
          title: 'Valor inválido',
          message: 'Informe uma taxa válida em R$/h.',
          variant: 'warning',
        });
        setSaving(false);
        return;
      }
      const payload = {
        name: companyForm.name.trim(),
        description: companyForm.description.trim() || null,
        default_hourly_rate: defaultRate,
      };
      if (editingCompanyId) {
        await api.put(`/api/projects/costs/companies/${editingCompanyId}`, payload);
      } else {
        await api.post('/api/projects/costs/companies', payload);
      }
      setCompanyForm({ name: '', description: '', default_hourly_rate: '' });
      await loadSnapshot();
      closeModal();
      showAlert({ title: 'Sucesso', message: editingCompanyId ? 'Empresa atualizada com sucesso.' : 'Empresa cadastrada com sucesso.', variant: 'success' });
    } catch (err: unknown) {
      handleApiError(err, 'Erro ao salvar empresa.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const defaultRate = parseReaisToCents(roleForm.default_hourly_rate);
      if (roleForm.default_hourly_rate && defaultRate == null) {
        showAlert({
          title: 'Valor inválido',
          message: 'Informe uma taxa válida em R$/h.',
          variant: 'warning',
        });
        setSaving(false);
        return;
      }
      const payload = {
        name: roleForm.name.trim(),
        description: roleForm.description.trim() || null,
        default_hourly_rate: defaultRate,
      };
      if (editingRoleId) {
        await api.put(`/api/projects/costs/roles/${editingRoleId}`, payload);
      } else {
        await api.post('/api/projects/costs/roles', payload);
      }
      setRoleForm({ name: '', description: '', default_hourly_rate: '' });
      await loadSnapshot();
      closeModal();
      showAlert({ title: 'Sucesso', message: editingRoleId ? 'Função atualizada com sucesso.' : 'Função cadastrada com sucesso.', variant: 'success' });
    } catch (err: unknown) {
      handleApiError(err, 'Erro ao salvar função.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCollaborator = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const overrideRate = parseReaisToCents(collaboratorForm.hourly_rate_override);
      if (collaboratorForm.hourly_rate_override && overrideRate == null) {
        showAlert({
          title: 'Valor inválido',
          message: 'Informe um override válido em R$/h.',
          variant: 'warning',
        });
        setSaving(false);
        return;
      }
      const payload = {
        name: collaboratorForm.name.trim(),
        email: collaboratorForm.email.trim() || null,
        jira_account_id: collaboratorForm.jira_account_id.trim() || null,
        jira_display_name: collaboratorForm.jira_display_name.trim() || null,
        user_id: collaboratorForm.user_id ? Number(collaboratorForm.user_id) : null,
        company_id: Number(collaboratorForm.company_id),
        role_id: collaboratorForm.role_id ? Number(collaboratorForm.role_id) : null,
        hourly_rate_override: overrideRate,
        is_third_party: collaboratorForm.is_third_party,
      };
      if (editingCollaboratorId) {
        await api.put(`/api/projects/costs/collaborators/${editingCollaboratorId}`, payload);
      } else {
        await api.post('/api/projects/costs/collaborators', payload);
      }
      setCollaboratorForm({
        name: '',
        email: '',
        jira_account_id: '',
        jira_display_name: '',
        user_id: '',
        company_id: '',
        role_id: '',
        hourly_rate_override: '',
        is_third_party: false,
      });
      await loadSnapshot();
      closeModal();
      showAlert({ title: 'Sucesso', message: editingCollaboratorId ? 'Colaborador atualizado com sucesso.' : 'Colaborador cadastrado com sucesso.', variant: 'success' });
    } catch (err: unknown) {
      handleApiError(err, 'Erro ao salvar colaborador.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateDemandRate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const hourlyRate = parseReaisToCents(demandRateForm.hourly_rate);
      if (hourlyRate == null) {
        showAlert({
          title: 'Valor inválido',
          message: 'Informe um valor válido em R$/h.',
          variant: 'warning',
        });
        setSaving(false);
        return;
      }
      const payload = {
        demand_type: demandRateForm.demand_type.trim(),
        label: demandRateForm.label.trim(),
        hourly_rate: hourlyRate,
      };
      if (editingDemandRateId) {
        await api.put(`/api/projects/costs/demand-rates/${editingDemandRateId}`, payload);
      } else {
        await api.post('/api/projects/costs/demand-rates', payload);
      }
      setDemandRateForm({ demand_type: 'OS', label: '', hourly_rate: '' });
      await loadSnapshot();
      closeModal();
      showAlert({ title: 'Sucesso', message: editingDemandRateId ? 'Taxa de demanda atualizada com sucesso.' : 'Taxa de demanda cadastrada com sucesso.', variant: 'success' });
    } catch (err: unknown) {
      handleApiError(err, 'Erro ao salvar taxa de demanda.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCompanyRoleRate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const hourlyRate = parseReaisToCents(companyRoleRateForm.hourly_rate);
      if (hourlyRate == null) {
        showAlert({
          title: 'Valor inválido',
          message: 'Informe uma taxa válida em R$/h.',
          variant: 'warning',
        });
        setSaving(false);
        return;
      }
      const payload = {
        company_id: Number(companyRoleRateForm.company_id),
        role_id: Number(companyRoleRateForm.role_id),
        hourly_rate: hourlyRate,
        effective_start: companyRoleRateForm.effective_start || null,
        effective_end: companyRoleRateForm.effective_end || null,
        notes: companyRoleRateForm.notes.trim() || null,
      };
      if (editingCompanyRoleRateId) {
        await api.put(`/api/projects/costs/company-role-rates/${editingCompanyRoleRateId}`, payload);
      } else {
        await api.post('/api/projects/costs/company-role-rates', payload);
      }
      setCompanyRoleRateForm({
        company_id: '',
        role_id: '',
        hourly_rate: '',
        effective_start: '',
        effective_end: '',
        notes: '',
      });
      await loadSnapshot();
      closeModal();
      showAlert({ title: 'Sucesso', message: editingCompanyRoleRateId ? 'Regra de taxa atualizada com sucesso.' : 'Regra de taxa cadastrada com sucesso.', variant: 'success' });
    } catch (err: unknown) {
      handleApiError(err, 'Erro ao salvar taxa por empresa/função.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCollaboratorRate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const hourlyRate = parseReaisToCents(collaboratorRateForm.hourly_rate);
      if (hourlyRate == null) {
        showAlert({
          title: 'Valor inválido',
          message: 'Informe uma taxa válida em R$/h.',
          variant: 'warning',
        });
        setSaving(false);
        return;
      }
      const payload = {
        collaborator_id: Number(collaboratorRateForm.collaborator_id),
        hourly_rate: hourlyRate,
        effective_start: collaboratorRateForm.effective_start || null,
        effective_end: collaboratorRateForm.effective_end || null,
        notes: collaboratorRateForm.notes.trim() || null,
      };
      if (editingCollaboratorRateId) {
        await api.put(`/api/projects/costs/collaborator-rates/${editingCollaboratorRateId}`, payload);
      } else {
        await api.post('/api/projects/costs/collaborator-rates', payload);
      }
      setCollaboratorRateForm({
        collaborator_id: '',
        hourly_rate: '',
        effective_start: '',
        effective_end: '',
        notes: '',
      });
      await loadSnapshot();
      closeModal();
      showAlert({
        title: 'Sucesso',
        message: editingCollaboratorRateId ? 'Taxa temporal do colaborador atualizada com sucesso.' : 'Taxa temporal do colaborador cadastrada com sucesso.',
        variant: 'success',
      });
    } catch (err: unknown) {
      handleApiError(err, 'Erro ao salvar taxa temporal do colaborador.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (path: string, confirmMessage: string) => {
    const confirmed = await showConfirm({
      title: 'Confirmar exclusão',
      message: confirmMessage,
      variant: 'danger',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!confirmed) return;

    setSaving(true);
    try {
      await api.delete(path);
      await loadSnapshot();
      showAlert({ title: 'Sucesso', message: 'Registro excluído com sucesso.', variant: 'success' });
    } catch (err: unknown) {
      handleApiError(err, 'Não foi possível excluir o registro.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAllCollaboratorsByCompany = async (companyId: number, companyName: string) => {
    const confirmed = await showConfirm({
      title: 'Excluir todos os colaboradores da empresa',
      message: `Esta ação excluirá todos os colaboradores da empresa "${companyName}" e também as taxas temporais desses colaboradores. Deseja continuar?`,
      variant: 'danger',
      confirmLabel: 'Excluir todos',
      cancelLabel: 'Cancelar',
    });
    if (!confirmed) return;

    setSaving(true);
    try {
      const { data } = await api.delete<{
        ok: boolean;
        deleted_collaborators: number;
        deleted_collaborator_rates: number;
      }>(`/api/projects/costs/collaborators/by-company/${companyId}`);
      await loadSnapshot();
      showAlert({
        title: 'Exclusão concluída',
        message: `Foram excluídos ${data.deleted_collaborators || 0} colaborador(es) e ${data.deleted_collaborator_rates || 0} taxa(s) temporal(is).`,
        variant: 'success',
      });
    } catch (err: unknown) {
      handleApiError(err, 'Não foi possível excluir os colaboradores da empresa.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditCompany = (item: { id: number; name: string; description?: string | null; default_hourly_rate?: number | null }) => {
    setEditingCompanyId(item.id);
    setCompanyForm({
      name: item.name,
      description: item.description || '',
      default_hourly_rate: centsToReaisInput(item.default_hourly_rate),
    });
    setActiveModal('company');
  };

  const handleEditRole = (item: { id: number; name: string; description?: string | null; default_hourly_rate?: number | null }) => {
    setEditingRoleId(item.id);
    setRoleForm({
      name: item.name,
      description: item.description || '',
      default_hourly_rate: centsToReaisInput(item.default_hourly_rate),
    });
    setActiveModal('role');
  };

  const handleEditCollaborator = (item: {
    id: number;
    name: string;
    email?: string | null;
    jira_account_id?: string | null;
    jira_display_name?: string | null;
    user_id?: number | null;
    company_id: number;
    role_id?: number | null;
    hourly_rate_override?: number | null;
    is_third_party: boolean;
  }) => {
    setEditingCollaboratorId(item.id);
    setCollaboratorForm({
      name: item.name,
      email: item.email || '',
      jira_account_id: item.jira_account_id || '',
      jira_display_name: item.jira_display_name || '',
      user_id: item.user_id ? String(item.user_id) : '',
      company_id: String(item.company_id),
      role_id: item.role_id ? String(item.role_id) : '',
      hourly_rate_override: centsToReaisInput(item.hourly_rate_override),
      is_third_party: item.is_third_party,
    });
    setActiveModal('collaborator');
  };

  const handleEditDemandRate = (item: { id: number; demand_type: string; label: string; hourly_rate: number }) => {
    setEditingDemandRateId(item.id);
    setDemandRateForm({
      demand_type: item.demand_type,
      label: item.label,
      hourly_rate: centsToReaisInput(item.hourly_rate),
    });
    setActiveModal('demandRate');
  };

  const handleEditCompanyRoleRate = (item: {
    id: number;
    company_id: number;
    role_id: number;
    hourly_rate: number;
    effective_start?: string | null;
    effective_end?: string | null;
    notes?: string | null;
  }) => {
    setEditingCompanyRoleRateId(item.id);
    setCompanyRoleRateForm({
      company_id: String(item.company_id),
      role_id: String(item.role_id),
      hourly_rate: centsToReaisInput(item.hourly_rate),
      effective_start: item.effective_start || '',
      effective_end: item.effective_end || '',
      notes: item.notes || '',
    });
    setActiveModal('companyRoleRate');
  };

  const handleEditCollaboratorRate = (item: {
    id: number;
    collaborator_id: number;
    hourly_rate: number;
    effective_start?: string | null;
    effective_end?: string | null;
    notes?: string | null;
  }) => {
    setEditingCollaboratorRateId(item.id);
    setCollaboratorRateForm({
      collaborator_id: String(item.collaborator_id),
      hourly_rate: centsToReaisInput(item.hourly_rate),
      effective_start: item.effective_start || '',
      effective_end: item.effective_end || '',
      notes: item.notes || '',
    });
    setActiveModal('collaboratorRate');
  };

  const openIssueObservationModal = (issueKey: string, issueObservation: string) => {
    setEditingIssueKey(issueKey);
    setIssueObservationInput(issueObservation || '');
  };

  const closeIssueObservationModal = () => {
    if (savingIssueObservation) return;
    setEditingIssueKey('');
    setIssueObservationInput('');
  };

  const handleSaveIssueObservation = async () => {
    const normalizedKey = editingIssueKey.trim().toUpperCase();
    if (!normalizedKey) return;

    setSavingIssueObservation(true);
    try {
      const payload = { observation: issueObservationInput.trim() || null };
      await api.put(
        `/api/projects/costs/issue-observations/${encodeURIComponent(normalizedKey)}`,
        payload,
      );
      setCalcResult((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          issues: prev.issues.map((item) => {
            if (item.issue_key !== normalizedKey) return item;
            return {
              ...item,
              issue_observation: (payload.observation ?? item.issue_summary),
              has_custom_observation: Boolean(payload.observation),
            };
          }),
        };
      });
      showAlert({ title: 'Sucesso', message: 'Observação salva com sucesso.', variant: 'success' });
      closeIssueObservationModal();
    } catch (err: unknown) {
      handleApiError(err, 'Erro ao salvar observação da issue.');
    } finally {
      setSavingIssueObservation(false);
    }
  };

  const handleCalculate = async () => {
    if (!jqlInput.trim()) {
      showAlert({
        title: 'JQL obrigatória',
        message: 'Informe uma JQL para calcular os custos.',
        variant: 'warning',
      });
      return;
    }
    if (worklogStartDateInput && worklogEndDateInput && worklogStartDateInput > worklogEndDateInput) {
      showAlert({
        title: 'Período inválido',
        message: 'A data inicial do período não pode ser maior que a data final.',
        variant: 'warning',
      });
      return;
    }
    let parsedMaxResults: number | null = null;
    if (!unlimitedIssues) {
      parsedMaxResults = Number(calcMaxResultsInput);
      if (!Number.isInteger(parsedMaxResults) || parsedMaxResults < 1) {
        showAlert({
          title: 'Limite inválido',
          message: 'Informe um limite de issues >= 1, ou marque "Sem limite".',
          variant: 'warning',
        });
        return;
      }
    }
    setCalculating(true);
    try {
      const { data } = await api.post<ProjectCostCalculationResponse>('/api/projects/costs/calculate', {
        jql: jqlInput.trim(),
        max_results: unlimitedIssues ? null : parsedMaxResults,
        worklog_start_date: worklogStartDateInput || null,
        worklog_end_date: worklogEndDateInput || null,
        include_subtask_worklogs: includeSubtaskWorklogs,
      }, { timeout: unlimitedIssues ? 0 : 600000 });
      setCalcResult(data);
      setAnalysisHistory((prev) => {
        const unmatchedHours = (data.unmatched_worklogs || []).reduce((sum, item) => sum + Number(item.hours || 0), 0);
        const nextItem: SavedCostAnalysis = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          createdAt: new Date().toISOString(),
          jql: jqlInput.trim(),
          maxResults: unlimitedIssues ? 0 : Number(parsedMaxResults || 0),
          worklogStartDate: worklogStartDateInput || '',
          worklogEndDate: worklogEndDateInput || '',
          totalHours: Number(data.total_hours || 0),
          totalCostCents: Number(data.total_cost_cents || 0),
          totalRevenueCents: Number(data.total_revenue_cents || 0),
          totalMarginCents: Number(data.total_margin_cents || 0),
          issuesCount: Number(data.issues?.length || 0),
          unmatchedHours,
        };
        return [nextItem, ...prev].slice(0, 20);
      });
      setAnalysisCollaboratorsPage(1);
      setAnalysisIssuesPage(1);
      navigate('/projects/costs/analise');
    } catch (err: unknown) {
      const ax = err as {
        code?: string;
        message?: string;
        response?: { status?: number; data?: { detail?: unknown } };
      };
      const detail = ax.response?.data?.detail;
      const detailText = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((item) => (typeof item === 'object' && item && 'msg' in item ? String((item as { msg: unknown }).msg) : String(item))).join(' ')
          : '';
      const status = ax.response?.status;
      let message = detailText || 'Erro ao calcular custos com dados do Jira.';
      if (!detailText && (status === 502 || status === 504 || ax.code === 'ECONNABORTED' || /timeout/i.test(ax.message || ''))) {
        message = (
          'O Jira demorou demais ou a conexão caiu no meio do cálculo. '
          + 'Use um período de worklog (ex.: últimos 30 dias / ano), desmarque subtarefas temporariamente '
          + 'ou rode por carteira em vez de JQL global sem limite.'
        );
      }
      showAlert({
        title: status === 502 || status === 504 ? 'Timeout no Jira' : 'Erro',
        message,
        variant: 'danger',
      });
      setCalcResult(null);
    } finally {
      setCalculating(false);
    }
  };

  const handleAskNl = async () => {
    const question = nlQuestion.trim();
    if (!question) {
      setNlError('Digite uma pergunta para a IA.');
      return;
    }
    setNlLoading(true);
    setNlError('');
    try {
      const { data } = await api.post<ProjectsNlQueryResponse>('/api/projects/costs/nl-query', {
        question,
        jira_jql_context: jqlInput,
        source_screen: 'projects_cost_management',
        max_jira_results: 25,
        max_db_rows: 50,
      });
      setNlResult(data);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setNlResult(null);
      setNlError(error?.response?.data?.detail || 'Não foi possível processar a pergunta.');
    } finally {
      setNlLoading(false);
    }
  };

  const handleResolveJiraIds = async () => {
    setResolveJiraStatus({
      running: true,
      stage: 'preview',
      progressPct: 15,
      message: 'Analisando colaboradores pendentes no Jira...',
    });
    setSaving(true);
    try {
      const preview = await api.post<ProjectCostCollaboratorResolveJiraIdsResponse>(
        '/api/projects/costs/collaborators/resolve-jira-ids',
        {
          dry_run: true,
          limit: 500,
          only_missing_account_id: true,
        }
      );
      const previewData = preview.data;
      const candidates = Number(previewData.total_candidates || 0);
      const resolved = Number(previewData.resolved || 0);
      const skipped = Number(previewData.skipped || 0);
      setResolveJiraStatus({
        running: true,
        stage: 'confirm',
        progressPct: 45,
        message: `Previa concluida: ${candidates} candidato(s), ${resolved} resolvido(s), ${skipped} pendencia(s).`,
      });
      if (candidates === 0) {
        setResolveJiraStatus({
          running: false,
          stage: 'done',
          progressPct: 100,
          message: 'Sem pendências de Jira ID para resolver.',
        });
        showAlert({
          title: 'Sem pendencias',
          message: 'Nao ha colaboradores pendentes de jira_account_id para resolver.',
          variant: 'info',
        });
        return;
      }
      setResolveJiraPreview(previewData);
      setShowResolveJiraPreviewModal(true);
      setManualMappingSelection({});
      setManualMappingSearch({});
      setManualMappingRemoteOptions({});
      setManualMappingRemoteLoading({});
      setJiraWorklogAuthors([]);
      setLoadingJiraWorklogAuthors(true);
      try {
        const authorsResp = await api.get<ProjectCostJiraWorklogAuthorsResponse>(
          `/api/projects/costs/collaborators/jira-worklog-authors?year=${jiraWorklogAuthorYear}`
        );
        setJiraWorklogAuthors(authorsResp.data.authors || []);
      } catch (err: unknown) {
        // Não bloqueia o fluxo: a busca manual remota por digitação continua disponível.
        setJiraWorklogAuthors([]);
      } finally {
        setLoadingJiraWorklogAuthors(false);
      }
      setResolveJiraStatus({
        running: false,
        stage: 'confirm',
        progressPct: 45,
        message: `Previa pronta: ${resolved} com potencial de resolucao e ${skipped} pendencia(s) para tratar.`,
      });
    } catch (err: unknown) {
      setResolveJiraStatus({
        running: false,
        stage: 'error',
        progressPct: 0,
        message: 'Falha ao resolver Jira IDs. Verifique o erro e tente novamente.',
      });
      handleApiError(err, 'Erro ao resolver jira_account_id automaticamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyResolveJiraIds = async () => {
    setResolveJiraStatus({
      running: true,
      stage: 'apply',
      progressPct: 70,
      message: 'Aplicando vinculos de Jira ID e e-mail nos colaboradores...',
    });
    setSaving(true);
    try {
      const applyResp = await api.post<ProjectCostCollaboratorResolveJiraIdsResponse>(
        '/api/projects/costs/collaborators/resolve-jira-ids',
        {
          dry_run: false,
          limit: 500,
          only_missing_account_id: true,
        }
      );
      const data = applyResp.data;
      setShowResolveJiraPreviewModal(false);
      setResolveJiraPreview(null);
      setResolveJiraStatus({
        running: true,
        stage: 'refresh',
        progressPct: 90,
        message: 'Atualizando a lista de colaboradores...',
      });
      await loadSnapshot();
      setResolveJiraStatus({
        running: false,
        stage: 'done',
        progressPct: 100,
        message: `Processo concluido. Atualizados ${data.updated} colaborador(es); pendencias: ${data.skipped}.`,
      });
      showAlert({
        title: 'Processo concluido',
        message: `Atualizados ${data.updated} colaborador(es). Resolvidos: ${data.resolved}. Pendentes: ${data.skipped}.`,
        variant: data.updated > 0 ? 'success' : 'warning',
      });
    } catch (err: unknown) {
      setResolveJiraStatus({
        running: false,
        stage: 'error',
        progressPct: 0,
        message: 'Falha ao aplicar resolucao automatica de Jira IDs.',
      });
      handleApiError(err, 'Erro ao aplicar resolucao automatica de jira_account_id.');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyManualMapping = async (collaboratorId: number) => {
    const selectedAccountId = (manualMappingSelection[collaboratorId] || '').trim();
    if (!selectedAccountId) {
      showAlert({
        title: 'Selecione um usuario Jira',
        message: 'Escolha um usuario Jira da lista antes de aplicar o mapeamento manual.',
        variant: 'warning',
      });
      return;
    }
    const localAuthor = jiraWorklogAuthors.find((item) => String(item.jira_account_id || '').trim() === selectedAccountId);
    const remoteAuthor = (manualMappingRemoteOptions[collaboratorId] || []).find((item) => String(item.account_id || '').trim() === selectedAccountId);
    const selectedAuthor = localAuthor
      ? {
          jira_account_id: localAuthor.jira_account_id,
          jira_display_name: localAuthor.jira_display_name,
          jira_email: localAuthor.jira_email,
        }
      : remoteAuthor
      ? {
          jira_account_id: remoteAuthor.account_id,
          jira_display_name: remoteAuthor.display_name,
          jira_email: remoteAuthor.email,
        }
      : null;
    if (!selectedAuthor || !String(selectedAuthor.jira_account_id || '').trim()) {
      showAlert({
        title: 'Usuario Jira invalido',
        message: 'O usuario Jira selecionado nao foi encontrado na lista atual.',
        variant: 'warning',
      });
      return;
    }
    const collaborator = collaborators.find((item) => item.id === collaboratorId);
    if (!collaborator) {
      showAlert({
        title: 'Colaborador nao encontrado',
        message: 'Nao foi possivel abrir o colaborador para ajuste manual.',
        variant: 'warning',
      });
      return;
    }

    const payload: Record<string, string> = {
      jira_account_id: String(selectedAuthor.jira_account_id || '').trim(),
      jira_display_name: selectedAuthor.jira_display_name,
    };
    if (!String(collaborator.email || '').trim() && selectedAuthor.jira_email) {
      payload.email = selectedAuthor.jira_email;
    }

    setMappingCollaboratorIdInProgress(collaboratorId);
    try {
      await api.put(`/api/projects/costs/collaborators/${collaboratorId}`, payload);
      await loadSnapshot();
      setResolveJiraPreview((prev) => {
        if (!prev) return prev;
        const nextItems = prev.items.map((item) => {
          if (item.collaborator_id !== collaboratorId) return item;
          return {
            ...item,
            status: 'resolved',
            reason: 'Mapeado manualmente nesta tela.',
            resolved_jira_account_id: payload.jira_account_id,
            resolved_jira_display_name: payload.jira_display_name,
            resolved_jira_email: payload.email || selectedAuthor.jira_email || null,
          };
        });
        const nextResolved = nextItems.filter((item) => item.status === 'resolved').length;
        const nextSkipped = nextItems.filter((item) => item.status === 'skipped').length;
        return {
          ...prev,
          resolved: nextResolved,
          skipped: nextSkipped,
          items: nextItems,
        };
      });
      setManualMappingSelection((prev) => ({ ...prev, [collaboratorId]: '' }));
      showAlert({
        title: 'Mapeamento aplicado',
        message: `Colaborador ${collaborator.name} mapeado manualmente com sucesso.`,
        variant: 'success',
      });
    } catch (err: unknown) {
      handleApiError(err, 'Erro ao aplicar mapeamento manual do colaborador.');
    } finally {
      setMappingCollaboratorIdInProgress(null);
    }
  };

  const handleManualMappingSearchChange = async (collaboratorId: number, value: string) => {
    setManualMappingSearch((prev) => ({ ...prev, [collaboratorId]: value }));
    const query = String(value || '').trim();
    if (query.length < 2) {
      setManualMappingRemoteOptions((prev) => ({ ...prev, [collaboratorId]: [] }));
      return;
    }
    setManualMappingRemoteLoading((prev) => ({ ...prev, [collaboratorId]: true }));
    try {
      const { data } = await api.get<ProjectCostJiraUserSearchResponse>(
        `/api/projects/costs/collaborators/jira-users/search?query=${encodeURIComponent(query)}&max_results=25`
      );
      setManualMappingRemoteOptions((prev) => ({ ...prev, [collaboratorId]: data.items || [] }));
    } catch (err: unknown) {
      setManualMappingRemoteOptions((prev) => ({ ...prev, [collaboratorId]: [] }));
    } finally {
      setManualMappingRemoteLoading((prev) => ({ ...prev, [collaboratorId]: false }));
    }
  };

  const activeCompanies = companies.filter((item) => item.is_active);
  const activeRoles = roles.filter((item) => item.is_active);
  const activeCollaboratorsCount = collaborators.filter((item) => item.is_active).length;
  const activeDemandRatesCount = demandRates.filter((item) => item.is_active).length;
  const activeCompanyRoleRatesCount = companyRoleRates.filter((item) => item.is_active).length;

  const setupDiagnostics = useMemo(() => {
    const activeCollaborators = collaborators.filter((item) => item.is_active);
    const mappedCollaborators = activeCollaborators.filter((item) => Boolean((item.jira_account_id || '').trim()) || Boolean((item.jira_display_name || '').trim()));
    const mappingCoveragePercent = activeCollaborators.length > 0
      ? (mappedCollaborators.length / activeCollaborators.length) * 100
      : 0;
    const checklist = [
      {
        id: 'rates-demand',
        label: 'Taxas OS/AS configuradas',
        ok: activeDemandRatesCount > 0,
        help: activeDemandRatesCount > 0
          ? `${activeDemandRatesCount} taxa(s) ativa(s).`
          : 'Cadastre pelo menos OS e AS para calcular receita.',
      },
      {
        id: 'collaborators',
        label: 'Colaboradores cadastrados',
        ok: activeCollaborators.length > 0,
        help: activeCollaborators.length > 0
          ? `${activeCollaborators.length} colaborador(es) ativo(s).`
          : 'Sem colaboradores ativos, o custo ficará zerado.',
      },
      {
        id: 'mapping',
        label: 'Mapeamento Jira dos colaboradores',
        ok: mappingCoveragePercent >= EXECUTIVE_MAPPING_MIN_COVERAGE_PCT,
        help: `${mappedCollaborators.length}/${activeCollaborators.length || 0} com accountId/display no Jira (${formatPercent(mappingCoveragePercent)}). Critîrio executivo: >= ${EXECUTIVE_MAPPING_MIN_COVERAGE_PCT}%.`,
      },
      {
        id: 'hourly-rates',
        label: 'Regras de taxa por empresa/função',
        ok: activeCompanyRoleRatesCount > 0,
        help: activeCompanyRoleRatesCount > 0
          ? `${activeCompanyRoleRatesCount} regra(s) ativa(s).`
          : 'Sem regra empresa+função, o sistema depende apenas de taxa de função/empresa/override.',
      },
    ];
    const okCount = checklist.filter((item) => item.ok).length;
    const readinessPercent = (okCount / checklist.length) * 100;
    return {
      checklist,
      readinessPercent,
      mappedCollaboratorsCount: mappedCollaborators.length,
      activeCollaboratorsCount: activeCollaborators.length,
    };
  }, [activeCompanyRoleRatesCount, activeDemandRatesCount, collaborators]);

  const updateTableState = useCallback((key: CostTableKey, patch: Partial<CostTableState>) => {
    setTableStates((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...patch,
      },
    }));
  }, []);

  const getPagedCompanies = useCallback((companyId?: number | null) => {
    const filtered = companies
      .filter((item) => !companyId || item.id === companyId)
      .filter((item) => matchesSearchTokens(tableStates.companies.query, [
        item.name,
        item.description,
        item.is_active ? 'ativa' : 'inativa',
      ]));
    return paginateItems(filtered, tableStates.companies.page, tableStates.companies.pageSize);
  }, [companies, tableStates.companies.page, tableStates.companies.pageSize, tableStates.companies.query]);

  const getPagedRoles = useCallback((companyId?: number | null) => {
    const roleIdsUsedInContext = companyId
      ? new Set([
          ...collaborators
            .filter((item) => item.company_id === companyId)
            .map((item) => item.role_id)
            .filter((item): item is number => Boolean(item)),
          ...companyRoleRates
            .filter((item) => item.company_id === companyId)
            .map((item) => item.role_id),
        ])
      : null;
    const filtered = roles
      .filter((item) => !roleIdsUsedInContext || roleIdsUsedInContext.has(item.id))
      .filter((item) => matchesSearchTokens(tableStates.roles.query, [
        item.name,
        item.description,
        item.is_active ? 'ativa' : 'inativa',
      ]));
    return paginateItems(filtered, tableStates.roles.page, tableStates.roles.pageSize);
  }, [
    collaborators,
    companyRoleRates,
    roles,
    tableStates.roles.page,
    tableStates.roles.pageSize,
    tableStates.roles.query,
  ]);

  const getPagedCollaborators = useCallback((companyId?: number | null) => {
    const filtered = collaborators
      .filter((item) => !companyId || item.company_id === companyId)
      .filter((item) => matchesSearchTokens(tableStates.collaborators.query, [
        item.name,
        item.email,
        item.jira_display_name,
        item.jira_account_id,
        item.user_id,
        item.company?.name,
        item.role?.name,
        item.is_third_party ? 'terceiro' : 'proprio',
      ]));
    return paginateItems(filtered, tableStates.collaborators.page, tableStates.collaborators.pageSize);
  }, [
    collaborators,
    tableStates.collaborators.page,
    tableStates.collaborators.pageSize,
    tableStates.collaborators.query,
  ]);

  const getPagedDemandRates = useCallback(() => {
    const filtered = demandRates.filter((item) => matchesSearchTokens(tableStates.demandRates.query, [
      item.demand_type,
      item.label,
      formatCurrencyFromCents(item.hourly_rate),
    ]));
    return paginateItems(filtered, tableStates.demandRates.page, tableStates.demandRates.pageSize);
  }, [demandRates, tableStates.demandRates.page, tableStates.demandRates.pageSize, tableStates.demandRates.query]);

  const getPagedCompanyRoleRates = useCallback((companyId?: number | null) => {
    const filtered = companyRoleRates
      .filter((item) => !companyId || item.company_id === companyId)
      .filter((item) => matchesSearchTokens(tableStates.companyRoleRates.query, [
        item.company?.name || item.company_id,
        item.role?.name || item.role_id,
        item.notes,
        item.effective_start,
        item.effective_end,
        formatCurrencyFromCents(item.hourly_rate),
      ]));
    return paginateItems(filtered, tableStates.companyRoleRates.page, tableStates.companyRoleRates.pageSize);
  }, [
    companyRoleRates,
    tableStates.companyRoleRates.page,
    tableStates.companyRoleRates.pageSize,
    tableStates.companyRoleRates.query,
  ]);

  const getPagedCollaboratorRates = useCallback((companyId?: number | null) => {
    const filtered = collaboratorRates
      .filter((item) => {
        if (!companyId) return true;
        const collaboratorCompanyId = item.collaborator?.company_id;
        return collaboratorCompanyId === companyId;
      })
      .filter((item) => matchesSearchTokens(tableStates.collaboratorRates.query, [
        item.collaborator?.name,
        item.collaborator?.company?.name,
        item.collaborator?.role?.name,
        item.effective_start,
        item.effective_end,
        item.notes,
        centsToReaisInput(item.hourly_rate),
      ]))
      .sort((a, b) => b.id - a.id);
    return paginateItems(filtered, tableStates.collaboratorRates.page, tableStates.collaboratorRates.pageSize);
  }, [
    collaboratorRates,
    tableStates.collaboratorRates.page,
    tableStates.collaboratorRates.pageSize,
    tableStates.collaboratorRates.query,
  ]);

  const companyStatsList = useMemo((): CompanyStats[] => (
    // Usa todas as empresas do snapshot (não só is_active) para os cards da visão geral
    // continuarem visíveis mesmo se o flag active vier inconsistente.
    companies.map((company) => {
      const companyCollaborators = collaborators.filter((item) => item.company_id === company.id);
      const activeCompanyCollaborators = companyCollaborators.filter((item) => item.is_active !== false);
      return {
        companyId: company.id,
        companyName: company.name,
        collaboratorsCount: companyCollaborators.length,
        companyRoleRatesCount: companyRoleRates.filter((item) => item.company_id === company.id).length,
        collaboratorRatesCount: collaboratorRates.filter((item) => item.collaborator?.company_id === company.id).length,
        mappingPercent: getCompanyMappingPercent(companyCollaborators),
        activeCollaboratorsCount: activeCompanyCollaborators.length,
      };
    })
  ), [companies, collaborators, companyRoleRates, collaboratorRates]);

  const getCompanyById = useCallback(
    (id: number) => companies.find((item) => item.id === id),
    [companies],
  );

  const openNewCompany = useCallback(() => {
    setEditingCompanyId(null);
    setCompanyForm({ name: '', description: '', default_hourly_rate: '' });
    setActiveModal('company');
  }, []);

  const openNewRole = useCallback(() => {
    setEditingRoleId(null);
    setRoleForm({ name: '', description: '', default_hourly_rate: '' });
    setActiveModal('role');
  }, []);

  const openNewCollaborator = useCallback((companyId?: string) => {
    const selectedCompany = activeCompanies.find((item) => String(item.id) === String(companyId || ''));
    setEditingCollaboratorId(null);
    setCollaboratorForm({
      name: '',
      email: '',
      jira_account_id: '',
      jira_display_name: '',
      user_id: '',
      company_id: companyId || '',
      role_id: '',
      hourly_rate_override: '',
      is_third_party: companyId ? inferIsThirdPartyFromCompanyName(selectedCompany?.name) : false,
    });
    setActiveModal('collaborator');
  }, [activeCompanies]);

  const openNewDemandRate = useCallback(() => {
    setEditingDemandRateId(null);
    setDemandRateForm({ demand_type: 'OS', label: '', hourly_rate: '' });
    setActiveModal('demandRate');
  }, []);

  const openNewCompanyRoleRate = useCallback((companyId?: string) => {
    setEditingCompanyRoleRateId(null);
    setCompanyRoleRateForm({
      company_id: companyId || '',
      role_id: '',
      hourly_rate: '',
      effective_start: '',
      effective_end: '',
      notes: '',
    });
    setActiveModal('companyRoleRate');
  }, []);

  const openNewCollaboratorRate = useCallback(() => {
    setEditingCollaboratorRateId(null);
    setCollaboratorRateForm({
      collaborator_id: '',
      hourly_rate: '',
      effective_start: '',
      effective_end: '',
      notes: '',
    });
    setActiveModal('collaboratorRate');
  }, []);

  const openNewCollaboratorRateForCompany = useCallback((_companyId: number) => {
    setEditingCollaboratorRateId(null);
    setCollaboratorRateForm({
      collaborator_id: '',
      hourly_rate: '',
      effective_start: '',
      effective_end: '',
      notes: '',
    });
    setActiveModal('collaboratorRate');
  }, []);

  const topCompanyByCost = useMemo(() => {
    if (!calcResult || calcResult.companies.length === 0) return null;
    return calcResult.companies.reduce((top, current) => (current.cost_cents > top.cost_cents ? current : top), calcResult.companies[0]);
  }, [calcResult]);

  const calcIssuesLimit = useMemo(() => {
    if (unlimitedIssues) return null;
    const parsed = Number(calcMaxResultsInput);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [calcMaxResultsInput, unlimitedIssues]);

  const coverageSummary = useMemo(() => {
    if (!calcResult) return null;
    const matchedHours = (calcResult.collaborators || []).reduce((sum, item) => sum + Number(item.hours || 0), 0);
    const unmatchedHours = (calcResult.unmatched_worklogs || []).reduce((sum, item) => sum + Number(item.hours || 0), 0);
    const trackedHours = matchedHours + unmatchedHours;
    const mappingCoveragePercent = trackedHours > 0 ? (matchedHours / trackedHours) * 100 : null;
    const issuesCount = calcResult.issues.length;
    const issueLimitReached = calcIssuesLimit != null && issuesCount >= calcIssuesLimit;
    return {
      matchedHours,
      unmatchedHours,
      trackedHours,
      mappingCoveragePercent,
      issuesCount,
      issueLimitReached,
    };
  }, [calcIssuesLimit, calcResult]);

  const executiveIssueRows = useMemo(() => {
    if (!calcResult) return [];
    return [...(calcResult.issues || [])]
      .map((item) => {
        const revenue = Number(item.revenue_cents || 0);
        const ownCost = Number(item.own_cost_cents || 0);
        const thirdCost = Number(item.third_party_cost_cents || 0);
        const cost = Number(item.cost_cents || 0);
        const margin = revenue - cost;
        const marginPct = revenue > 0 ? (margin / revenue) * 100 : null;
        return {
          issueKey: item.issue_key,
          summary: item.issue_summary,
          status: item.issue_status || '',
          demandType: item.demand_type,
          hours: Number(item.hours || 0),
          revenueCents: revenue,
          ownCostCents: ownCost,
          thirdCostCents: thirdCost,
          totalCostCents: cost,
          marginCents: margin,
          marginPct,
          observation: item.issue_observation || item.issue_summary,
        };
      })
      .sort((a, b) => b.revenueCents - a.revenueCents);
  }, [calcResult]);

  const executiveTotals = useMemo(() => {
    if (!calcResult) return null;
    const criticalIssues = executiveIssueRows.filter((row) => row.marginPct != null && row.marginPct <= criticalMarginPctThreshold).length;
    const approvalIssues = executiveIssueRows.filter((row) => row.status.toLowerCase().includes('aprova')).length;
    const marginPct = calcResult.total_revenue_cents > 0
      ? (calcResult.total_margin_cents / calcResult.total_revenue_cents) * 100
      : null;
    return {
      totalRevenueCents: calcResult.total_revenue_cents,
      totalOwnCostCents: calcResult.total_own_cost_cents,
      totalThirdPartyCostCents: calcResult.total_third_party_cost_cents,
      totalCostCents: calcResult.total_cost_cents,
      totalMarginCents: calcResult.total_margin_cents,
      totalMarginPct: marginPct,
      criticalIssues,
      approvalIssues,
    };
  }, [calcResult, criticalMarginPctThreshold, executiveIssueRows]);

  const previousAnalysis = useMemo(() => {
    if (analysisHistory.length < 2) return null;
    return analysisHistory[1];
  }, [analysisHistory]);

  const applyHistoryFilters = (item: SavedCostAnalysis) => {
    setJqlInput(item.jql);
    const historyUnlimited = !item.maxResults;
    setUnlimitedIssues(historyUnlimited);
    setCalcMaxResultsInput(String(item.maxResults || 200));
    setWorklogStartDateInput(item.worklogStartDate || '');
    setWorklogEndDateInput(item.worklogEndDate || '');
    showAlert({
      title: 'Filtros reaplicados',
      message: 'Os filtros foram carregados no formulário. Clique em "Calcular" para atualizar os números.',
      variant: 'info',
    });
  };

  const removeHistoryItem = (historyId: string) => {
    setAnalysisHistory((prev) => prev.filter((item) => item.id !== historyId));
  };

  const pagedAnalysisCollaborators = useMemo(
    () => paginateItems(calcResult?.collaborators ?? [], analysisCollaboratorsPage, analysisCollaboratorsPageSize),
    [analysisCollaboratorsPage, analysisCollaboratorsPageSize, calcResult?.collaborators],
  );

  const pagedAnalysisIssues = useMemo(
    () => paginateItems(calcResult?.issues ?? [], analysisIssuesPage, analysisIssuesPageSize),
    [analysisIssuesPage, analysisIssuesPageSize, calcResult?.issues],
  );

  const contextValue = useMemo<ProjectCostContextValue>(() => ({
    loading,
    configSyncing,
    saving,
    calculating,
    loadSnapshot,
    companies,
    roles,
    collaborators,
    demandRates,
    companyRoleRates,
    collaboratorRates,
    activeCompanies,
    activeRoles,
    activeCollaboratorsCount,
    setupDiagnostics,
    companyStatsList,
    tableStates,
    updateTableState,
    getPagedCompanies,
    getPagedRoles,
    getPagedCollaborators,
    getPagedDemandRates,
    getPagedCompanyRoleRates,
    getPagedCollaboratorRates,
    getCompanyById,
    openNewCompany,
    openNewRole,
    openNewCollaborator,
    openNewDemandRate,
    openNewCompanyRoleRate,
    openNewCollaboratorRate,
    openNewCollaboratorRateForCompany,
    handleEditCompany,
    handleEditRole,
    handleEditCollaborator,
    handleEditDemandRate,
    handleEditCompanyRoleRate,
    handleEditCollaboratorRate,
    handleDelete,
    handleDeleteAllCollaboratorsByCompany,
    handleResolveJiraIds,
    resolveJiraStatus,
    showCollaboratorImportModal,
    setShowCollaboratorImportModal,
    showCollaboratorJiraImportModal,
    setShowCollaboratorJiraImportModal,
    handleExportCostConfig,
    handleImportCostConfig,
    calcResult,
    jqlInput,
    setJqlInput,
    calcMaxResultsInput,
    setCalcMaxResultsInput,
    unlimitedIssues,
    setUnlimitedIssues,
    worklogStartDateInput,
    setWorklogStartDateInput,
    worklogEndDateInput,
    setWorklogEndDateInput,
    includeSubtaskWorklogs,
    setIncludeSubtaskWorklogs,
    handleCalculate,
    analysisControlTab,
    setAnalysisControlTab,
    nlQuestion,
    setNlQuestion,
    nlLoading,
    nlError,
    nlResult,
    handleAskNl,
    executiveTotals,
    executiveIssueRows,
    executiveCoverageThresholdPct,
    setExecutiveCoverageThresholdPct,
    criticalMarginPctThreshold,
    setCriticalMarginPctThreshold,
    coverageSummary,
    previousAnalysis,
    calcIssuesLimit,
    topCompanyByCost,
    showDetailedAnalysis,
    setShowDetailedAnalysis,
    pagedAnalysisCollaborators,
    analysisCollaboratorsPage,
    setAnalysisCollaboratorsPage,
    analysisCollaboratorsPageSize,
    setAnalysisCollaboratorsPageSize,
    pagedAnalysisIssues,
    analysisIssuesPage,
    setAnalysisIssuesPage,
    analysisIssuesPageSize,
    setAnalysisIssuesPageSize,
    showObservationColumn,
    setShowObservationColumn,
    openIssueObservationModal,
    analysisHistory,
    setAnalysisHistory,
    applyHistoryFilters,
    removeHistoryItem,
    navigate,
  }), [
    loading, configSyncing, saving, calculating, loadSnapshot, companies, roles, collaborators, demandRates,
    companyRoleRates, collaboratorRates, activeCompanies, activeRoles, activeCollaboratorsCount,
    setupDiagnostics, companyStatsList, tableStates, updateTableState, getPagedCompanies, getPagedRoles,
    getPagedCollaborators, getPagedDemandRates, getPagedCompanyRoleRates, getPagedCollaboratorRates,
    getCompanyById, openNewCompany, openNewRole, openNewCollaborator, openNewDemandRate,
    openNewCompanyRoleRate, openNewCollaboratorRate, openNewCollaboratorRateForCompany,
    calcResult, jqlInput, calcMaxResultsInput, unlimitedIssues, worklogStartDateInput, worklogEndDateInput, resolveJiraStatus,
    includeSubtaskWorklogs, handleCalculate, analysisControlTab, nlQuestion, nlLoading, nlError,
    nlResult, handleAskNl, executiveTotals, executiveIssueRows, executiveCoverageThresholdPct,
    criticalMarginPctThreshold, coverageSummary, previousAnalysis, calcIssuesLimit, topCompanyByCost,
    showDetailedAnalysis, pagedAnalysisCollaborators, analysisCollaboratorsPage,
    analysisCollaboratorsPageSize, pagedAnalysisIssues, analysisIssuesPage, analysisIssuesPageSize,
    showObservationColumn, analysisHistory, applyHistoryFilters, removeHistoryItem, navigate,
    showCollaboratorImportModal, showCollaboratorJiraImportModal, handleExportCostConfig, handleImportCostConfig,
    handleDeleteAllCollaboratorsByCompany,
  ]);

  if (!canAccessCosts) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <ProjectCostContext.Provider value={contextValue}>
      <div className="projects-cost-page">
        {loading ? (
          <div className="py-5 text-center">
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          <Routes>
            <Route element={<ProjectsCostShell />}>
              <Route index element={<ProjectsCostHubPage />} />
              <Route path="empresas/:companyId" element={<ProjectsCostCompanyLayout />}>
                <Route index element={<Navigate to="resumo" replace />} />
                <Route path="resumo" element={<CompanySummaryPage />} />
                <Route path="colaboradores" element={<CompanyCollaboratorsPage />} />
                <Route path="regras" element={<CompanyRulesPage />} />
                <Route path="taxas-temporais" element={<CompanyTemporalRatesPage />} />
                <Route path="funcoes-usadas" element={<CompanyRolesUsedPage />} />
              </Route>
              <Route path="configuracoes" element={<ProjectsCostSettingsLayout />}>
                <Route index element={<Navigate to="empresas" replace />} />
                <Route path="empresas" element={<SettingsCompaniesPage />} />
                <Route path="funcoes" element={<SettingsRolesPage />} />
                <Route path="taxas-demanda" element={<SettingsDemandRatesPage />} />
              </Route>
              <Route path="analise" element={<ProjectsCostAnalysisPage />} />
              <Route path="historico" element={<ProjectsCostHistoryPage />} />
            </Route>
          </Routes>
        )}

      <Modal show={Boolean(editingIssueKey)} onHide={closeIssueObservationModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Observação da issue {editingIssueKey || ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Observação</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={issueObservationInput}
              onChange={(e) => setIssueObservationInput(e.target.value)}
              placeholder="Escreva aqui a observação da issue. Se limpar e salvar, volta para o texto padrão do Jira."
            />
            <Form.Text className="text-muted">
              Esta observação aparece na análise de custos e pode sobrescrever o texto padrão vindo do Jira.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeIssueObservationModal} disabled={savingIssueObservation}>
            Cancelar
          </Button>
          <Button onClick={handleSaveIssueObservation} disabled={savingIssueObservation}>
            {savingIssueObservation ? 'Salvando...' : 'Salvar observação'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showResolveJiraPreviewModal}
        onHide={() => {
          if (saving) return;
          setShowResolveJiraPreviewModal(false);
          setResolveJiraStatus({
            running: false,
            stage: 'idle',
            progressPct: 0,
            message: 'Resolucao de Jira IDs cancelada pelo usuario.',
          });
        }}
        centered
        size="xl"
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Resolver Jira IDs automaticamente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div className="fw-semibold">Previa da resolucao</div>
            <div className="text-muted small">
              Analisados {resolveJiraPreview?.total_candidates || 0} colaborador(es). Com potencial de resolucao:{' '}
              {resolveJiraPreview?.resolved || 0}. Pendentes para ajuste manual: {resolveJiraPreview?.skipped || 0}.
            </div>
            <div className="text-muted small mt-1">
              Base para mapeamento manual: usuarios Jira com worklogs em {jiraWorklogAuthorYear}.
            </div>
            <div className="mt-2 d-flex align-items-center gap-2">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={loadingJiraWorklogAuthors}
                onClick={async () => {
                  setLoadingJiraWorklogAuthors(true);
                  try {
                    const authorsResp = await api.get<ProjectCostJiraWorklogAuthorsResponse>(
                      `/api/projects/costs/collaborators/jira-worklog-authors?year=${jiraWorklogAuthorYear}`
                    );
                    setJiraWorklogAuthors(authorsResp.data.authors || []);
                  } catch (err: unknown) {
                    handleApiError(err, 'Erro ao recarregar usuarios Jira com horas.');
                  } finally {
                    setLoadingJiraWorklogAuthors(false);
                  }
                }}
              >
                {loadingJiraWorklogAuthors ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Carregando usuarios Jira...
                  </>
                ) : (
                  'Recarregar lista Jira'
                )}
              </Button>
              <span className="small text-muted">
                {jiraWorklogAuthors.length} usuario(s) encontrados.
              </span>
            </div>
          </div>

          <div className="mb-2 fw-semibold">Pendentes por ambiguidade/ausencia</div>
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            <Table bordered hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Colaborador</th>
                  <th>Motivo</th>
                  <th>Sugestao Jira</th>
                  <th style={{ width: 360 }}>Mapeamento manual</th>
                </tr>
              </thead>
              <tbody>
                {(resolveJiraPreview?.items || [])
                  .filter((item) => item.status === 'skipped')
                  .map((item, idx) => (
                    <tr key={`${item.collaborator_id}-${idx}`}>
                      <td>{item.collaborator_name}</td>
                      <td>{item.reason || 'Sem detalhes'}</td>
                      <td>
                        {item.resolved_jira_display_name || item.resolved_jira_account_id || item.resolved_jira_email
                          ? `${item.resolved_jira_display_name || 'Sem nome'} | ${item.resolved_jira_account_id || 'Sem accountId'} | ${item.resolved_jira_email || 'Sem e-mail'}`
                          : 'Sem sugestao automatica'}
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-2">
                          <Form.Control
                            size="sm"
                            placeholder="Digite nome, accountId ou e-mail para filtrar..."
                            value={manualMappingSearch[item.collaborator_id] || ''}
                            onChange={(e) => {
                              void handleManualMappingSearchChange(item.collaborator_id, e.target.value);
                            }}
                            disabled={mappingCollaboratorIdInProgress === item.collaborator_id}
                          />
                          {manualMappingRemoteLoading[item.collaborator_id] && (
                            <div className="small text-muted">
                              Buscando usuários no Jira...
                            </div>
                          )}
                          <div className="d-flex gap-2">
                          <Form.Select
                            size="sm"
                            value={manualMappingSelection[item.collaborator_id] || ''}
                            onChange={(e) => {
                              const selected = e.target.value;
                              setManualMappingSelection((prev) => ({
                                ...prev,
                                [item.collaborator_id]: selected,
                              }));
                            }}
                            disabled={mappingCollaboratorIdInProgress === item.collaborator_id}
                          >
                            <option value="">Selecione um usuario Jira...</option>
                            {[...((manualMappingRemoteOptions[item.collaborator_id] || []).map((author) => ({
                                jira_account_id: author.account_id,
                                jira_display_name: author.display_name,
                                jira_email: author.email,
                                hours: null as number | null,
                              }))), ...jiraWorklogAuthors]
                              .filter((author, idx, arr) => {
                                const accountId = String(author.jira_account_id || '').trim();
                                if (!accountId) return false;
                                return arr.findIndex((item) => String(item.jira_account_id || '').trim() === accountId) === idx;
                              })
                              .filter((author) => {
                                const rawSearch = String(manualMappingSearch[item.collaborator_id] || '').trim().toLowerCase();
                                if (!rawSearch) return true;
                                const accountId = String(author.jira_account_id || '').toLowerCase();
                                const displayName = String(author.jira_display_name || '').toLowerCase();
                                const email = String(author.jira_email || '').toLowerCase();
                                return displayName.includes(rawSearch) || accountId.includes(rawSearch) || email.includes(rawSearch);
                              })
                              .slice(0, 200)
                              .map((author) => {
                              const accountId = String(author.jira_account_id || '').trim();
                              if (!accountId) return null;
                              const emailLabel = author.jira_email ? ` - ${author.jira_email}` : '';
                              const hoursLabel = author.hours != null ? ` - ${author.hours}h` : '';
                              const label = `${author.jira_display_name} (${accountId})${emailLabel}${hoursLabel}`;
                              return (
                                <option key={accountId} value={accountId}>
                                  {label}
                                </option>
                              );
                            })}
                          </Form.Select>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => void handleApplyManualMapping(item.collaborator_id)}
                            disabled={
                              mappingCollaboratorIdInProgress === item.collaborator_id
                              || !manualMappingSelection[item.collaborator_id]
                            }
                          >
                            {mappingCollaboratorIdInProgress === item.collaborator_id ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Salvando...
                              </>
                            ) : (
                              'Aplicar'
                            )}
                          </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                {(resolveJiraPreview?.items || []).filter((item) => item.status === 'skipped').length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-muted text-center">
                      Nenhum colaborador pendente nesta previa.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          {resolveJiraStatus.running && (
            <div className="mt-3">
              <div className="small text-muted mb-1">{resolveJiraStatus.message}</div>
              <ProgressBar now={Math.max(0, Math.min(100, resolveJiraStatus.progressPct))} label={`${Math.round(resolveJiraStatus.progressPct)}%`} />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              if (saving) return;
              setShowResolveJiraPreviewModal(false);
              setResolveJiraStatus({
                running: false,
                stage: 'idle',
                progressPct: 0,
                message: 'Resolucao de Jira IDs cancelada pelo usuario.',
              });
            }}
            disabled={saving}
          >
            Fechar
          </Button>
          <Button variant="primary" onClick={() => void handleApplyResolveJiraIds()} disabled={saving || resolveJiraStatus.running}>
            {saving || resolveJiraStatus.running ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Aplicando...
              </>
            ) : (
              'Aplicar resolucao automatica'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === 'company'} onHide={closeModal} centered>
        <Form onSubmit={handleCreateCompany}>
          <Modal.Header closeButton>
            <Modal.Title>{editingCompanyId ? 'Editar empresa' : 'Nova empresa'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-2">
              <Form.Label>Nome</Form.Label>
              <Form.Control
                value={companyForm.name}
                onChange={(e) => setCompanyForm((prev) => ({ ...prev, name: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Descrição</Form.Label>
              <Form.Control
                value={companyForm.description}
                onChange={(e) => setCompanyForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Taxa padrão (R$/h)</Form.Label>
              <Form.Control
                type="text"
                inputMode="decimal"
                placeholder="Ex.: 224,85"
                value={companyForm.default_hourly_rate}
                onChange={(e) => setCompanyForm((prev) => ({ ...prev, default_hourly_rate: e.target.value }))}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeModal}>Cancelar</Button>
            <Button type="submit" disabled={saving}>Salvar</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={activeModal === 'role'} onHide={closeModal} centered>
        <Form onSubmit={handleCreateRole}>
          <Modal.Header closeButton>
            <Modal.Title>{editingRoleId ? 'Editar função' : 'Nova função'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-2">
              <Form.Label>Nome</Form.Label>
              <Form.Control
                value={roleForm.name}
                onChange={(e) => setRoleForm((prev) => ({ ...prev, name: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Descrição</Form.Label>
              <Form.Control
                value={roleForm.description}
                onChange={(e) => setRoleForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Taxa padrão (R$/h)</Form.Label>
              <Form.Control
                type="text"
                inputMode="decimal"
                placeholder="Ex.: 205,51"
                value={roleForm.default_hourly_rate}
                onChange={(e) => setRoleForm((prev) => ({ ...prev, default_hourly_rate: e.target.value }))}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeModal}>Cancelar</Button>
            <Button type="submit" disabled={saving}>Salvar</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={activeModal === 'collaborator'} onHide={closeModal} centered size="lg">
        <Form onSubmit={handleCreateCollaborator}>
          <Modal.Header closeButton>
            <Modal.Title>{editingCollaboratorId ? 'Editar colaborador' : 'Novo colaborador'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Nome</Form.Label>
                  <Form.Control
                    value={collaboratorForm.name}
                    onChange={(e) => setCollaboratorForm((prev) => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>E-mail</Form.Label>
                  <Form.Control
                    value={collaboratorForm.email}
                    onChange={(e) => setCollaboratorForm((prev) => ({ ...prev, email: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Display Jira</Form.Label>
                  <Form.Control
                    value={collaboratorForm.jira_display_name}
                    onChange={(e) => setCollaboratorForm((prev) => ({ ...prev, jira_display_name: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Jira accountId</Form.Label>
                  <Form.Control
                    value={collaboratorForm.jira_account_id}
                    onChange={(e) => setCollaboratorForm((prev) => ({ ...prev, jira_account_id: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>ID do usuário (vínculo interno)</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={collaboratorForm.user_id}
                    onChange={(e) => setCollaboratorForm((prev) => ({ ...prev, user_id: e.target.value }))}
                    placeholder="Opcional. Ex.: 123"
                  />
                  <Form.Text className="text-muted">
                    Use para ligar este colaborador ao cadastro interno de usuários.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Empresa</Form.Label>
                  <Form.Select
                    value={collaboratorForm.company_id}
                    onChange={(e) => {
                      const nextCompanyId = e.target.value;
                      const selectedCompany = activeCompanies.find((item) => String(item.id) === String(nextCompanyId));
                      setCollaboratorForm((prev) => ({
                        ...prev,
                        company_id: nextCompanyId,
                        is_third_party: editingCollaboratorId
                          ? prev.is_third_party
                          : inferIsThirdPartyFromCompanyName(selectedCompany?.name),
                      }));
                    }}
                    required
                  >
                    <option value="">Selecione</option>
                    {activeCompanies.map((company) => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Função</Form.Label>
                  <Form.Select
                    value={collaboratorForm.role_id}
                    onChange={(e) => setCollaboratorForm((prev) => ({ ...prev, role_id: e.target.value }))}
                  >
                    <option value="">Selecione</option>
                    {activeRoles.map((role) => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Taxa override (R$/h)</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    placeholder="Ex.: 180,00"
                    value={collaboratorForm.hourly_rate_override}
                    onChange={(e) => setCollaboratorForm((prev) => ({ ...prev, hourly_rate_override: e.target.value }))}
                  />
                  <Form.Text className="text-muted">
                    Opcional. Se preencher, esta taxa substitui a taxa da empresa/função para este colaborador.
                    Se deixar vazio, o sistema usa as regras padrão (empresa + função, função ou empresa).
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Check
                  id="cost-modal-third-party"
                  label="Colaborador terceiro"
                  checked={collaboratorForm.is_third_party}
                  onChange={(e) => setCollaboratorForm((prev) => ({ ...prev, is_third_party: e.target.checked }))}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeModal}>Cancelar</Button>
            <Button type="submit" disabled={saving}>Salvar</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ProjectCostCollaboratorImportModal
        show={showCollaboratorImportModal}
        onHide={() => setShowCollaboratorImportModal(false)}
        onImported={loadSnapshot}
        showAlert={showAlert}
      />
      <ProjectCostCollaboratorImportJiraUsersModal
        show={showCollaboratorJiraImportModal}
        onHide={() => setShowCollaboratorJiraImportModal(false)}
        onImported={loadSnapshot}
        companies={activeCompanies}
        roles={activeRoles}
        showAlert={showAlert}
      />

      <Modal show={activeModal === 'demandRate'} onHide={closeModal} centered>
        <Form onSubmit={handleCreateDemandRate}>
          <Modal.Header closeButton>
            <Modal.Title>{editingDemandRateId ? 'Editar taxa OS/AS' : 'Nova taxa OS/AS'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-2">
              <Form.Label>Tipo</Form.Label>
              <Form.Select
                value={demandRateForm.demand_type}
                onChange={(e) => setDemandRateForm((prev) => ({ ...prev, demand_type: e.target.value }))}
                required
              >
                <option value="OS">OS</option>
                <option value="AS">AS</option>
                <option value="OUTROS">OUTROS</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Rótulo</Form.Label>
              <Form.Control
                value={demandRateForm.label}
                onChange={(e) => setDemandRateForm((prev) => ({ ...prev, label: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Valor por hora (R$/h)</Form.Label>
              <Form.Control
                type="text"
                inputMode="decimal"
                placeholder="Ex.: 401,90"
                value={demandRateForm.hourly_rate}
                onChange={(e) => setDemandRateForm((prev) => ({ ...prev, hourly_rate: e.target.value }))}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeModal}>Cancelar</Button>
            <Button type="submit" disabled={saving}>Salvar</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={activeModal === 'companyRoleRate'} onHide={closeModal} centered size="lg">
        <Form onSubmit={handleCreateCompanyRoleRate}>
          <Modal.Header closeButton>
            <Modal.Title>{editingCompanyRoleRateId ? 'Editar regra empresa + função' : 'Nova regra empresa + função'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Empresa</Form.Label>
                  <Form.Select
                    value={companyRoleRateForm.company_id}
                    onChange={(e) => setCompanyRoleRateForm((prev) => ({ ...prev, company_id: e.target.value }))}
                    required
                  >
                    <option value="">Selecione</option>
                    {activeCompanies.map((company) => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Função</Form.Label>
                  <Form.Select
                    value={companyRoleRateForm.role_id}
                    onChange={(e) => setCompanyRoleRateForm((prev) => ({ ...prev, role_id: e.target.value }))}
                    required
                  >
                    <option value="">Selecione</option>
                    {activeRoles.map((role) => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Taxa/h (R$/h)</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    placeholder="Ex.: 174,79"
                    value={companyRoleRateForm.hourly_rate}
                    onChange={(e) => setCompanyRoleRateForm((prev) => ({ ...prev, hourly_rate: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Vigência início</Form.Label>
                  <Form.Control
                    type="date"
                    value={companyRoleRateForm.effective_start}
                    onChange={(e) => setCompanyRoleRateForm((prev) => ({ ...prev, effective_start: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Vigência fim</Form.Label>
                  <Form.Control
                    type="date"
                    value={companyRoleRateForm.effective_end}
                    onChange={(e) => setCompanyRoleRateForm((prev) => ({ ...prev, effective_end: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Observação</Form.Label>
                  <Form.Control
                    value={companyRoleRateForm.notes}
                    onChange={(e) => setCompanyRoleRateForm((prev) => ({ ...prev, notes: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeModal}>Cancelar</Button>
            <Button type="submit" disabled={saving}>Salvar</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={activeModal === 'collaboratorRate'} onHide={closeModal} centered size="lg">
        <Form onSubmit={handleCreateCollaboratorRate}>
          <Modal.Header closeButton>
            <Modal.Title>{editingCollaboratorRateId ? 'Editar taxa temporal do colaborador' : 'Nova taxa temporal do colaborador'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-2">
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Colaborador</Form.Label>
                  <Form.Select
                    value={collaboratorRateForm.collaborator_id}
                    onChange={(e) => setCollaboratorRateForm((prev) => ({ ...prev, collaborator_id: e.target.value }))}
                    required
                  >
                    <option value="">Selecione</option>
                    {collaborators
                      .filter((item) => item.is_active)
                      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
                      .map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name} - {item.company?.name || 'Sem empresa'}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Taxa/h (R$/h)</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    placeholder="Ex.: 174,79"
                    value={collaboratorRateForm.hourly_rate}
                    onChange={(e) => setCollaboratorRateForm((prev) => ({ ...prev, hourly_rate: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Vigência início</Form.Label>
                  <Form.Control
                    type="date"
                    value={collaboratorRateForm.effective_start}
                    onChange={(e) => setCollaboratorRateForm((prev) => ({ ...prev, effective_start: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Vigência fim</Form.Label>
                  <Form.Control
                    type="date"
                    value={collaboratorRateForm.effective_end}
                    onChange={(e) => setCollaboratorRateForm((prev) => ({ ...prev, effective_end: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Observação</Form.Label>
                  <Form.Control
                    value={collaboratorRateForm.notes}
                    onChange={(e) => setCollaboratorRateForm((prev) => ({ ...prev, notes: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeModal}>Cancelar</Button>
            <Button type="submit" disabled={saving}>Salvar</Button>
          </Modal.Footer>
        </Form>
      </Modal>
      </div>
    </ProjectCostContext.Provider>
  );
};

export default ProjectsCostManagement;
